import axios from 'axios';
import { API_BASE_URL } from '../config';
import socket from '../services/socket';

// Create API instance with base URL
const tradingApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Execute a trading order (buy/sell/short/cover)
 * @param {string} userId - User ID
 * @param {string} stockId - Stock ID
 * @param {string} type - Order type ('LONG' or 'SHORT')
 * @param {number} shares - Number of shares to trade
 * @returns {Promise<Object>} - Order result with updated balance and position
 */
export const executeOrder = async (userId, stockId, type, shares) => {
  try {
    const response = await tradingApi.post('/api/trade', {
      userId,
      stockId,
      type,
      shares
    });

    // Make sure we have a valid price before emitting the event
    const price = response.data.position && response.data.position.entryPrice
      ? parseFloat(response.data.position.entryPrice)
      : null;

    if (price && !isNaN(price)) {
      // Emit trade event with validated price
      socket.emit('trade-executed', {
        userId,
        stockId,
        type,
        shares,
        price: price,
        timestamp: Date.now()
      });
    } else {
      console.warn('Missing or invalid price in trade response, cannot emit trade event');
    }

    return response.data;
  } catch (error) {
    console.error('Trade execution error:', error);

    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to execute trade. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get user positions and portfolio value
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User positions and account summary
 */
export const getUserPositions = async (userId) => {
  // Early validation - don't make API call with invalid userId
  if (!userId) {
    throw new Error('Invalid user ID');
  }

  try {
    const response = await tradingApi.get(`/api/positions/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching positions:', error);

    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to fetch portfolio data. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Close a specific position
 * @param {string} userId - User ID
 * @param {string} positionId - Position ID to close
 * @returns {Promise<Object>} - Result of closing the position
 */
// In your trading.js service file, modify the closePosition function:

export const closePosition = async (userId, positionId) => {
  try {
    // Make the API call
    const response = await tradingApi.post('/api/close-position', {
      userId,
      positionId
    });

    // Log the response to debug
    console.log('Close position response:', response.data);

    // Ensure the profit/loss is a valid number
    let profitLoss = 0;
    if (response.data && response.data.profitLoss !== undefined) {
      profitLoss = parseFloat(response.data.profitLoss);
      if (isNaN(profitLoss)) profitLoss = 0;
    }

    // Ensure the balance is a valid number
    let newBalance = 0;
    if (response.data && response.data.balance !== undefined) {
      newBalance = parseFloat(response.data.balance);
      if (isNaN(newBalance)) newBalance = 0;
    }

    // Return properly formatted data
    return {
      ...response.data,
      profitLoss: profitLoss,
      balance: newBalance,
      positions: response.data.positions || []
    };
  } catch (error) {
    console.error('Error closing position:', error);
    throw new Error(error.response?.data?.error || 'Failed to close position');
  }
};

/**
 * Get user's trading history
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of trades to return
 * @returns {Promise<Array>} - Array of trade records
 */
export const getTradeHistory = async (userId, limit = 20) => {
  try {
    const response = await tradingApi.get(`/api/trade-history/${userId}`, {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching trade history:', error);

    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to fetch trade history. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get transaction history for a user
 * @param {string} userId - User ID
 * @param {number} limit - Limit number of transactions (default: 50)
 * @returns {Promise<Object>} - Transaction history
 */
export const getTransactionHistory = async (userId, limit = 50) => {
  try {
    const response = await tradingApi.get(`/api/transaction-history/${userId}`, {
      params: { limit }
    });

    // Ensure numeric values are properly processed before returning
    if (response.data && response.data.transactions) {
      response.data.transactions = response.data.transactions.map(transaction => ({
        ...transaction,
        amount: parseFloat(transaction.amount) || 0,
        balance_before: parseFloat(transaction.balance_before) || 0,
        balance_after: parseFloat(transaction.balance_after) || 0
      }));
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching transaction history:', error);

    // Extract error message from response if available
    const errorMessage = error.response?.data?.error || 'Failed to fetch transaction history. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Subscribe to position updates via socket
 * @param {Function} onUpdate - Callback function for position updates
 * @returns {Function} - Cleanup function to unsubscribe
 */
export const subscribeToPositions = (onUpdate) => {
  const handlePositionUpdate = (data) => {
    if (typeof onUpdate === 'function') {
      onUpdate(data);
    }
  };

  socket.on('position-update', handlePositionUpdate);
  socket.emit('get-user-data'); // Request initial data

  // Return cleanup function
  return () => {
    socket.off('position-update', handlePositionUpdate);
  };
};

/**
 * Subscribe to market trading pressure data (admin feature)
 * @param {Function} onPressureUpdate - Callback for pressure updates
 * @returns {Function} - Cleanup function
 */
export const subscribeToPressureData = (onPressureUpdate) => {
  const handlePressureData = (data) => {
    if (typeof onPressureUpdate === 'function') {
      onPressureUpdate(data);
    }
  };

  socket.on('trading-pressure-data', handlePressureData);
  socket.emit('get-trading-pressure'); // Request initial data

  // Return cleanup function
  return () => {
    socket.off('trading-pressure-data', handlePressureData);
  };
};

export default {
  executeOrder,
  getUserPositions,
  closePosition,
  getTradeHistory,
  getTransactionHistory,
  subscribeToPositions,
  subscribeToPressureData
};